/*
 * Copyright (c) 2023 Puratos. All rights reserved.
 */

import * as React from 'react';
import { FunctionComponent } from 'react';
import ReactDOM from 'react-dom';
import { useSelector } from 'react-redux';
import {
    fetchingSKUsAction,
    isLoadingByScope,
} from '../../../selectors/loader';
import { getHomepage } from '../../../selectors/generic';
import { RoleMatrix } from '../../molecules/ecom/RoleMatrix';
import { ROLES } from '../../../data/user.model';
import ProductDetailsRestricted from '../../molecules/products/ProductDetailsRestricted';
import { isUserLoggedIn } from '../../../selectors/auth';
import { ECOMVariationPricing } from './ECOMVariationPricing';
import { ECOMVariationActions } from './ECOMVariationActions';
import { ProductsInCartOverlay } from '../../molecules/finder/ProductsInCartOverlay';
import { GTMContextProvider } from '../../../analytics/ga4/utils/GTMContext';
import {
    getActiveSku,
    getActiveSkuFromVariations,
    getActiveSkuNumber,
    getSkus,
    Store,
} from '../../../selectors/productPage';
import { IECOMVariation } from '../../../interfaces/organisms/product-page/IECOMVariaton';
import { IHomepage } from '../../../interfaces/aem/IHomepage';
import {
    IPackaging,
    IVariation,
} from '../../../interfaces/my-puratos/IGeneric';
import { WithSkeleton } from '../../my-puratos/hoc/WithSkeleton';
import { SkeletonTypes } from '../../atoms/effects/skeletons/utils/SkeletonTypes';
import { GA4InteractionType } from '../../../analytics/ga4/types/interaction';

const holderDiv = document.createElement('div');
holderDiv.id = 'carousel-quantity-holder';
document.querySelector('.carousel-overview .p-slick-holder')?.append(holderDiv);

export const ECOMVariation: FunctionComponent<IECOMVariation> = ({
    variations,
    obsolete,
    productCategoryTag
}) => {
    const homepage: IHomepage = useSelector(getHomepage);
    const loggedIn: boolean = useSelector(isUserLoggedIn);
    const loading: boolean = useSelector((state) =>
        isLoadingByScope(state, fetchingSKUsAction())
    );
    const activeVariation: IVariation = useSelector((state: Store) =>
        getActiveSkuFromVariations(state, variations)
    );
    const activeSkuNumber: number = useSelector((state: Store) =>
        getActiveSkuNumber(state)
    );
    const activeSku: IPackaging = useSelector((state: Store) =>
        getActiveSku(state)
    );
    const skus: IPackaging[] = useSelector((state: Store) => getSkus(state));

    if (!loggedIn) {
        return (
            <ProductDetailsRestricted
                loggedIn={loggedIn}
                variations={variations}
                obsolete={obsolete}
            />
        );
    }

    if (
        (!variations && !skus) ||
        activeSkuNumber === undefined ||
        (!activeVariation && !activeSku)
    ) {
        console.warn('ECOM Component not rendered, missing product data');
    }

    return (
        <WithSkeleton
            loading={loading}
            skeletonType={SkeletonTypes.ECOM_VARIATION}
        >
            <RoleMatrix>
                <RoleMatrix.Fragment roles={ROLES.restricted}>
                    <ProductDetailsRestricted
                        loggedIn={loggedIn}
                        variations={variations}
                        obsolete={obsolete}
                    />
                </RoleMatrix.Fragment>
                <RoleMatrix.Fragment roles={ROLES.valid}>
                    <GTMContextProvider type={GA4InteractionType.pdp}>
                        {ReactDOM.createPortal(
                            <ProductsInCartOverlay
                                id={activeVariation?.materialNumber}
                                rounded="xs"
                                fading
                            />,
                            holderDiv
                        )}
                        <ECOMVariationPricing
                            homepage={homepage}
                            variations={variations}
                            productCategoryTag={productCategoryTag}
                        />
                        <ECOMVariationActions variations={variations} />
                    </GTMContextProvider>
                </RoleMatrix.Fragment>
            </RoleMatrix>
        </WithSkeleton>
    );
};
